import {Component, inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {DatePicker} from '@core/components';
import {CompliancePlan} from '@core/types';
import {ModalService, PlanService} from '@core/services';

@Component({
  selector: 'core-plan-extension-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DatePicker],
  templateUrl: './plan-extension-form.html',
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;padding: 0 1.5rem 1.5rem'
  }
})
export class PlanExtensionForm implements OnInit {
  @Input() plan: CompliancePlan | null = null;

  form: FormGroup;
  isSubmitting = false;
  errorMessage = '';

  private readonly modal = inject(ModalService);
  private readonly planService = inject(PlanService);
  private readonly formBuilder = inject(FormBuilder);

  constructor() {
    this.form = this.formBuilder.group({
      newEndDate: [null, [Validators.required]]
    });
  }

  ngOnInit(): void {
    if (this.plan?.endDate) {
      this.form.patchValue({newEndDate: this.plan.endDate});
    }
  }

  async submit() {
    if (this.isSubmitting) return;
    if (!this.plan?.id) {
      this.errorMessage = 'No se encontró el plan activo.';
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const currentEnd = this.plan.endDate ? new Date(this.plan.endDate).getTime() : null;
    const nextEndRaw = this.form.value.newEndDate;
    const nextEnd = nextEndRaw ? new Date(nextEndRaw).getTime() : null;
    if (!nextEnd || Number.isNaN(nextEnd)) {
      this.errorMessage = 'Selecciona una fecha de fin válida.';
      return;
    }
    if (currentEnd && nextEnd <= currentEnd) {
      this.errorMessage = 'La nueva fecha de fin debe ser posterior a la fecha actual del plan.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const response = await this.planService.updatePlan(this.plan.id, {
      name: this.plan.name || '',
      description: this.plan.description || undefined,
      goals: this.plan.goals || undefined,
      startDate: this.plan.startDate || null,
      endDate: new Date(nextEnd).toISOString(),
      status: this.plan.status || undefined
    }, {skipLoader: true});
    this.isSubmitting = false;

    if (!response.success) {
      this.errorMessage = response.message || 'No se pudo registrar la prórroga del plan.';
      return;
    }

    if (response.result) {
      this.modal.closeTop(response.result);
      return;
    }
    const refreshed = await this.planService.getPlan(this.plan.id, {skipLoader: true});
    this.modal.closeTop(refreshed.result || true);
  }

  close() {
    this.modal.closeTop(false);
  }
}
